"use client";

import { useAtom } from "jotai";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { TourCard } from "./card";
import { TourOverlay } from "./overlay";
import { tourCompletedAtom } from "./storage";
import type { RegisteredTour, TourState, TourStepDefinition } from "./types";

interface TourKitContextValue {
  activeStep: TourStepDefinition | null;
  activeTour: RegisteredTour | null;
  finish: () => void;
  hasCompleted: (tourId: string) => boolean;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  prev: () => void;
  registerTour: (tour: RegisteredTour) => void;
  start: (tourId: string) => void;
  stepIndex: number;
  stop: () => void;
  totalSteps: number;
  unregisterTour: (tourId: string) => void;
}

const TourKitContext = createContext<TourKitContextValue | null>(null);

export function useTourKitContext(): TourKitContextValue {
  const ctx = useContext(TourKitContext);
  if (!ctx) {
    throw new Error("useTourKitContext must be used inside <TourKitProvider>");
  }
  return ctx;
}

export function TourKitProvider({ children }: { children: ReactNode }) {
  const tours = useRef<Map<string, RegisteredTour>>(new Map());
  const [state, setState] = useState<TourState>({
    activeTourId: null,
    stepIndex: 0,
  });

  const registerTour = useCallback((tour: RegisteredTour) => {
    tours.current.set(tour.id, tour);
  }, []);

  const unregisterTour = useCallback((tourId: string) => {
    tours.current.delete(tourId);
    setState((prev) =>
      prev.activeTourId === tourId ? { activeTourId: null, stepIndex: 0 } : prev
    );
  }, []);

  const start = useCallback((tourId: string) => {
    if (!tours.current.has(tourId)) {
      throw new Error(`Tour "${tourId}" is not registered`);
    }
    setState({ activeTourId: tourId, stepIndex: 0 });
  }, []);

  const stop = useCallback(() => {
    setState({ activeTourId: null, stepIndex: 0 });
  }, []);

  const next = useCallback(() => {
    setState((prev) => {
      const tour = prev.activeTourId
        ? tours.current.get(prev.activeTourId)
        : null;
      if (!tour) {
        return prev;
      }
      const nextIndex = prev.stepIndex + 1;
      if (nextIndex >= tour.steps.length) {
        return prev;
      }
      return { ...prev, stepIndex: nextIndex };
    });
  }, []);

  const prev = useCallback(() => {
    setState((p) => ({ ...p, stepIndex: Math.max(0, p.stepIndex - 1) }));
  }, []);

  return (
    <TourKitContext.Provider
      value={useTourKitValue(state, setState, tours, {
        registerTour,
        unregisterTour,
        start,
        stop,
        next,
        prev,
      })}
    >
      {children}
      <TourKitPortal />
    </TourKitContext.Provider>
  );
}

interface TourKitActions {
  next: () => void;
  prev: () => void;
  registerTour: (tour: RegisteredTour) => void;
  start: (tourId: string) => void;
  stop: () => void;
  unregisterTour: (tourId: string) => void;
}

function useTourKitValue(
  state: TourState,
  setState: (updater: (prev: TourState) => TourState) => void,
  tours: React.RefObject<Map<string, RegisteredTour>>,
  actions: TourKitActions
): TourKitContextValue {
  const activeTour = state.activeTourId
    ? (tours.current?.get(state.activeTourId) ?? null)
    : null;
  const activeStep = activeTour?.steps[state.stepIndex] ?? null;
  const totalSteps = activeTour?.steps.length ?? 0;
  const isFirst = state.stepIndex === 0;
  const isLast = totalSteps > 0 && state.stepIndex === totalSteps - 1;

  const finish = useCallback(() => {
    setState((prev) => {
      if (!prev.activeTourId) {
        return prev;
      }
      return { activeTourId: null, stepIndex: 0 };
    });
  }, [setState]);

  const hasCompleted = useCallback((_tourId: string) => false, []);

  return useMemo(
    () => ({
      ...actions,
      finish,
      activeTour,
      activeStep,
      stepIndex: state.stepIndex,
      totalSteps,
      isFirst,
      isLast,
      hasCompleted,
    }),
    [
      actions,
      finish,
      activeTour,
      activeStep,
      state.stepIndex,
      totalSteps,
      isFirst,
      isLast,
      hasCompleted,
    ]
  );
}

function TourKitPortal() {
  const ctx = useContext(TourKitContext);
  const activeTour = ctx?.activeTour ?? null;
  const activeTourId = activeTour?.id ?? "__none__";
  const [completed, setCompleted] = useAtom(tourCompletedAtom(activeTourId));

  const markCompleted = useCallback(() => {
    if (activeTour?.persist && !completed) {
      setCompleted(true);
    }
  }, [activeTour?.persist, activeTourId, completed, setCompleted]);

  if (!(ctx?.activeTour && ctx.activeStep)) {
    return null;
  }

  const onFinish = () => {
    ctx.finish();
  };

  return createPortal(
    <>
      <TourOverlay step={ctx.activeStep} />
      <TourCard
        isFirst={ctx.isFirst}
        isLast={ctx.isLast}
        onClose={onFinish}
        onFinish={onFinish}
        onNext={ctx.next}
        onPrev={ctx.prev}
        onShown={markCompleted}
        step={ctx.activeStep}
        stepIndex={ctx.stepIndex}
        totalSteps={ctx.totalSteps}
      />
    </>,
    document.body
  );
}
